import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  IconButton,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  ListAlt as ListAltIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Block as ExcludeIcon,
  CheckCircleOutline as IncludeIcon,
} from '@mui/icons-material';
import { RecordItem, OperationType } from '../zones/BusinessMapTab';
import ExportExclusionDialog, { setExportExclusion } from './ExportExclusionDialog';
import RecordEditDialog from './RecordEditDialog';
import { TableColumn, TableFilterRow, TableFilterState, TableSearchField } from '../table/TableControls';
import { bandText, effectiveRate, formatRate, isBanded } from '../../utils/bandRate';

interface Option {
  id: string;
  name: string;
}

interface FeatureOption extends Option {
  category: string;
}

// RecordDto (the actual API response shape) includes archivedDate, but the
// shared RecordItem type (defined for BusinessMapTab, which only ever shows
// non-archived records) doesn't declare it. A line's categoryNames is sent
// by the records lists only.
interface RecordWithArchive extends RecordItem {
  archivedDate: string | null;
  excludedFromExport: boolean;
  exportExclusionNote: string | null;
  products: (RecordItem['products'][number] & { categoryNames?: string[] })[];
}

// One grid row: a record's product line (GET api/records/lines), or the
// record alone when it has none. Edit and Archive act on the whole record.
interface LineRow {
  record: RecordWithArchive;
  line: RecordWithArchive['products'][number] | null;
}

const NO_AREA = 'No area';
const HAS_AREA = 'Has area';
const BANDED = 'Banded';
const NO_BAND = 'Whole area';
const EXPORT_INCLUDED = 'Included';
const EXPORT_EXCLUDED = 'Excluded';

const formatArea = (ha: number | null) => (ha == null ? '-' : ha.toLocaleString(undefined, { maximumFractionDigits: 2 }));

// The filter row's columns (see the add-table-grid skill). Filtering runs
// on the server (GET api/records/lines): text columns are "contains",
// select columns send the picked name's id, and Application rate takes 4,
// >5, <0.1, >=2, <=2 or 2-4. Product, Application rate, Unit and Category
// filter the lines.
const columns: TableColumn<LineRow>[] = [
  { key: 'date', label: 'Date', getValue: (r) => new Date(r.record.timeFrom).toLocaleDateString(), filter: 'none' },
  { key: 'business', label: 'Business', getValue: (r) => r.record.businessName ?? '', filter: 'select' },
  { key: 'farm', label: 'Farm', getValue: (r) => r.record.farmName ?? '' },
  { key: 'field', label: 'Field', getValue: (r) => r.record.fieldName ?? r.record.locationName ?? '' },
  { key: 'season', label: 'Season', getValue: (r) => r.record.timePeriodName ?? '', filter: 'select' },
  { key: 'operation', label: 'Operation', getValue: (r) => r.record.operationTypeName, filter: 'select' },
  { key: 'product', label: 'Product Name', getValue: (r) => r.line?.productName ?? '' },
  { key: 'rate', label: 'Application rate/ha', getValue: (r) => (r.line ? String(r.line.rate) : ''), placeholder: '>5, <0.1, 2-4' },
  { key: 'unit', label: 'Unit', getValue: (r) => r.line?.rateUnitName ?? '', filter: 'select' },
  { key: 'band', label: 'Band', getValue: (r) => (isBanded(r.record.bandPercent) ? BANDED : NO_BAND), filter: 'select' },
  { key: 'effectiveRate', label: 'Effective rate/ha', getValue: (r) => (r.line ? formatRate(effectiveRate(r.line.rate, r.record.bandPercent)) : ''), filter: 'none' },
  { key: 'category', label: 'Category', getValue: (r) => (r.line?.categoryNames ?? []).join(', '), filter: 'select' },
  { key: 'area', label: 'Area (ha)', getValue: (r) => (r.record.effectiveAreaHectares ? HAS_AREA : NO_AREA), filter: 'select' },
  { key: 'region', label: 'Region', getValue: (r) => r.record.linkedRegionName ?? r.record.regionName ?? '', filter: 'select' },
  { key: 'export', label: 'Export', getValue: (r) => (r.record.excludedFromExport ? EXPORT_EXCLUDED : EXPORT_INCLUDED), filter: 'select' },
];

const RIGHT_ALIGNED = ['area', 'rate', 'band', 'effectiveRate'];

// Every column sorts on the server (GET api/records/lines' sortBy), so the
// order holds across pages. Filter a product and sort its rate to bring its
// outliers to the top.
type SortKey = (typeof columns)[number]['key'] | 'status';

const RecordManagement: React.FC = () => {
  const auth = useAuth();
  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);

  // Filter options. The row filters by name; idOf maps a picked name back.
  const [businesses, setBusinesses] = useState<Option[]>([]);
  const [operationTypes, setOperationTypes] = useState<OperationType[]>([]);
  const [timePeriods, setTimePeriods] = useState<Option[]>([]);
  const [regions, setRegions] = useState<Option[]>([]);
  const [categories, setCategories] = useState<Option[]>([]);
  const [units, setUnits] = useState<Option[]>([]);

  const [rows, setRows] = useState<LineRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  // What the last fetch used: the search box and filters, 350 ms after typing stops.
  const [applied, setApplied] = useState<{ search: string; filters: Record<string, string> }>({ search: '', filters: {} });
  const [includeChildBusinesses, setIncludeChildBusinesses] = useState(true);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [sortBy, setSortBy] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // Only the latest fetch's answer is shown: a slower, older one (a wide
  // search) could otherwise land after it.
  const latestFetch = useRef(0);

  // The Add/Edit dialog (RecordEditDialog): open, and which record (null =
  // new, for the filtered business). An edited record's own business gives
  // the dialog its farms and fields.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<RecordWithArchive | null>(null);
  const [features, setFeatures] = useState<FeatureOption[]>([]);
  // A record to leave out of the export, in ExportExclusionDialog.
  const [excludeTarget, setExcludeTarget] = useState<string[] | null>(null);

  const idOf =(options: Option[], name: string | undefined) => (name ? options.find((o) => o.name === name)?.id : undefined);
  const businessId = idOf(businesses, filters.business) ?? null;
  const dialogBusinessId = editing?.businessId ?? businessId;

  useEffect(() => {
    if (!auth.isSignedIn || !auth.tokens?.accessToken) return;
    (async () => {
      const [businessRes, tpRes, opRes, regionRes, categoryRes, unitRes] = await Promise.all([
        fetch('/api/businesses/list', { headers: authHeaders() }),
        fetch('/api/time-periods', { headers: authHeaders() }),
        fetch('/api/operation-types', { headers: authHeaders() }),
        fetch('/api/regions', { headers: authHeaders() }),
        fetch('/api/product-categories', { headers: authHeaders() }),
        fetch('/api/units', { headers: authHeaders() }),
      ]);
      const named = (items: any[]) => items.filter((i) => !i.archivedDate).map((i) => ({ id: i.id, name: i.name }));
      if (businessRes.ok) setBusinesses((await businessRes.json()).map((b: any) => ({ id: b.id, name: b.name })));
      if (tpRes.ok) setTimePeriods((await tpRes.json()).map((t: any) => ({ id: t.id, name: t.name })));
      if (opRes.ok) setOperationTypes(await opRes.json());
      if (regionRes.ok) setRegions(named(await regionRes.json()));
      if (categoryRes.ok) setCategories(named(await categoryRes.json()));
      if (unitRes.ok) setUnits(named(await unitRes.json()));
    })();
  }, [auth.isSignedIn, auth.tokens?.accessToken, authHeaders]);

  useEffect(() => {
    if (!dialogOpen || !dialogBusinessId) {
      setFeatures([]);
      return;
    }
    (async () => {
      const [layersRes, featuresRes] = await Promise.all([
        fetch(`/api/businesses/${dialogBusinessId}/spatial-layers`, { headers: authHeaders() }),
        fetch(`/api/businesses/${dialogBusinessId}/spatial-features`, { headers: authHeaders() }),
      ]);
      if (!layersRes.ok || !featuresRes.ok) return;
      const layers = await layersRes.json();
      const layerCategoryById = new Map(layers.map((l: any) => [l.id, l.category]));
      const allFeatures = await featuresRes.json();
      setFeatures(
        allFeatures.map((f: any) => ({ id: f.id, name: f.name, category: layerCategoryById.get(f.spatialLayerId) || '' }))
      );
    })();
  }, [dialogOpen, dialogBusinessId, authHeaders]);

  useEffect(() => {
    const timer = setTimeout(() => setApplied({ search, filters }), 350);
    return () => clearTimeout(timer);
  }, [search, filters]);

  const fetchRecords = useCallback(async () => {
    const fetchId = ++latestFetch.current;
    setLoading(true);
    setError(null);
    try {
      const f = applied.filters;
      const params = new URLSearchParams({
        page: String(page + 1),
        pageSize: String(pageSize),
        includeArchived: String(includeArchived),
        includeChildBusinesses: String(includeChildBusinesses),
        sortBy,
        sortDir,
      });
      const set = (key: string, value: string | undefined) => { if (value?.trim()) params.set(key, value.trim()); };
      set('search', applied.search);
      set('businessId', idOf(businesses, f.business));
      set('farm', f.farm);
      set('field', f.field);
      set('timePeriodId', idOf(timePeriods, f.season));
      set('operationTypeId', idOf(operationTypes, f.operation));
      set('product', f.product);
      set('rate', f.rate);
      set('productCategoryId', idOf(categories, f.category));
      set('rateUnitId', idOf(units, f.unit));
      set('area', f.area === NO_AREA ? 'none' : f.area === HAS_AREA ? 'some' : undefined);
      set('band', f.band === BANDED ? 'banded' : f.band === NO_BAND ? 'none' : undefined);
      set('export', f.export === EXPORT_EXCLUDED ? 'excluded' : f.export === EXPORT_INCLUDED ? 'included' : undefined);
      set('regionId', idOf(regions, f.region));
      const res = await fetch(`/api/records/lines?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to fetch records');
      const data = await res.json();
      if (fetchId !== latestFetch.current) return;
      setRows(data.items.map((item: { record: RecordWithArchive; lineId: string | null }) => ({
        record: item.record,
        line: item.record.products.find((p) => p.id === item.lineId) ?? null,
      })));
      setTotalCount(data.totalCount);
    } catch (err) {
      if (fetchId === latestFetch.current) setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      if (fetchId === latestFetch.current) setLoading(false);
    }
    // idOf reads the option lists listed here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applied, page, pageSize, sortBy, sortDir, includeChildBusinesses, includeArchived, businesses, timePeriods, operationTypes, categories, units, regions, authHeaders]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const filterState: TableFilterState = {
    filters,
    setFilter: (key, value) => { setFilters((prev) => ({ ...prev, [key]: value })); setPage(0); },
    filterOptions: {
      business: businesses.map((b) => b.name),
      season: timePeriods.map((t) => t.name),
      operation: operationTypes.map((t) => t.name),
      category: categories.map((c) => c.name),
      unit: units.map((u) => u.name),
      area: [NO_AREA, HAS_AREA],
      band: [BANDED, NO_BAND],
      region: regions.map((r) => r.name),
      export: [EXPORT_INCLUDED, EXPORT_EXCLUDED],
    },
    hasActiveFilters: Object.values(filters).some((v) => v?.trim()),
    clearFilters: () => { setFilters({}); setPage(0); },
  };

  // A new column starts ascending; Date starts newest first.
  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(key === 'date' ? 'desc' : 'asc');
    }
    setPage(0);
  };

  const sortableHeader = (key: SortKey, label: string, align?: 'right') => (
    <TableCell key={key} align={align} sortDirection={sortBy === key ? sortDir : false}>
      <TableSortLabel active={sortBy === key} direction={sortBy === key ? sortDir : 'asc'} onClick={() => handleSort(key)}>
        {label}
      </TableSortLabel>
    </TableCell>
  );

  const openCreateDialog = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEditDialog = (record: RecordWithArchive) => {
    setEditing(record);
    setDialogOpen(true);
  };

  const handleArchive = async (record: RecordWithArchive) => {
    const lines = record.products.length;
    if (!window.confirm(lines > 1 ? `Archive this record? All ${lines} of its product lines go with it.` : 'Archive this record?')) return;
    try {
      const res = await fetch(`/api/records/${record.id}`, { method: 'DELETE', headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to archive record');
      await fetchRecords();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleRestore = async (id: string) => {
    try {
      const res = await fetch(`/api/records/${id}/restore`, { method: 'POST', headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to restore record');
      await fetchRecords();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleIncludeInExport = async (id: string) => {
    try {
      await setExportExclusion([id], false, null, authHeaders);
      await fetchRecords();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  return (
    <Box>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={1.5} mb={2}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <ListAltIcon />
          Records
        </Typography>
        <Tooltip title={businessId ? '' : 'Pick a Business in the filter row first'}>
          <span>
            <Button variant="contained" startIcon={<AddIcon />} disabled={!businessId} onClick={openCreateDialog}>
              Add Record
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1.5} mb={2}>
        <TableSearchField value={search} onChange={(value) => { setSearch(value); setPage(0); }} placeholder="Search farm or product…" />
        {businessId && (
          <FormControlLabel
            control={<Switch size="small" checked={includeChildBusinesses} onChange={(e) => { setIncludeChildBusinesses(e.target.checked); setPage(0); }} />}
            label="Include child businesses"
          />
        )}
        <FormControlLabel
          control={<Switch size="small" checked={includeArchived} onChange={(e) => { setIncludeArchived(e.target.checked); setPage(0); }} />}
          label="Show Archived"
        />
        {loading && <CircularProgress size={20} />}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper>
        <TableContainer>
          <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.5, px: 1, fontSize: '0.8125rem' }, '& .MuiTableCell-head': { fontWeight: 600, whiteSpace: 'nowrap' } }}>
            <TableHead>
              <TableRow>
                {columns.map((c) => sortableHeader(c.key, c.label, RIGHT_ALIGNED.includes(c.key) ? 'right' : undefined))}
                {sortableHeader('status', 'Status')}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
              <TableFilterRow columns={columns} state={filterState} trailingCells={2} />
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length + 2} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {loading ? 'Loading…' : search.trim() || filterState.hasActiveFilters ? 'No records match.' : 'No records found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map(({ record, line }) => (
                <TableRow key={`${record.id}-${line?.id ?? ''}`} hover onDoubleClick={() => !record.archivedDate && openEditDialog(record)}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(record.timeFrom).toLocaleDateString()}</TableCell>
                  <TableCell>{record.businessName || '-'}</TableCell>
                  <TableCell>{record.farmName || '-'}</TableCell>
                  <TableCell>{record.fieldName || record.locationName || '-'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{record.timePeriodName || '-'}</TableCell>
                  <TableCell>{record.operationTypeName}</TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>
                    {/* The rest of a tank mix, on hover. */}
                    <Tooltip
                      title={record.products.length > 1
                        ? record.products.map((p) => `${p.productName} ${formatRate(p.rate)} ${p.rateUnitName}`).join(' · ')
                        : ''}
                    >
                      <Typography variant="inherit" noWrap>{line?.productName ?? '-'}</Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">{line ? formatRate(line.rate) : '-'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{line?.rateUnitName ?? '-'}</TableCell>
                  <TableCell align="right">{bandText(record.bandPercent)}</TableCell>
                  <TableCell align="right">{line ? formatRate(effectiveRate(line.rate, record.bandPercent)) : '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 180 }}>
                    <Typography variant="inherit" noWrap>{(line?.categoryNames ?? []).join(', ') || '-'}</Typography>
                  </TableCell>
                  <TableCell align="right">{formatArea(record.effectiveAreaHectares)}</TableCell>
                  <TableCell>{record.linkedRegionName || record.regionName || '-'}</TableCell>
                  <TableCell>
                    {record.excludedFromExport ? (
                      <Tooltip title={record.exportExclusionNote ?? ''}>
                        <Chip label="Excluded" size="small" color="warning" />
                      </Tooltip>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    {record.archivedDate ? <Chip label="Archived" size="small" /> : <Chip label="Active" size="small" color="success" />}
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {record.archivedDate ? (
                      <Tooltip title="Restore">
                        <IconButton size="small" onClick={() => handleRestore(record.id)}><RestoreIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    ) : (
                      <>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEditDialog(record)}><EditIcon fontSize="small" /></IconButton>
                        </Tooltip>
                        {record.excludedFromExport ? (
                          <Tooltip title="Include in the export">
                            <IconButton size="small" onClick={() => handleIncludeInExport(record.id)}><IncludeIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Exclude from the export">
                            <IconButton size="small" onClick={() => setExcludeTarget([record.id])}><ExcludeIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Archive">
                          <IconButton size="small" color="error" onClick={() => handleArchive(record)}><DeleteIcon fontSize="small" /></IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>

      <ExportExclusionDialog
        recordIds={excludeTarget}
        authHeaders={authHeaders}
        onClose={() => setExcludeTarget(null)}
        onDone={fetchRecords}
        onError={setError}
      />

      <RecordEditDialog
        open={dialogOpen}
        businessId={dialogBusinessId}
        recordId={editing?.id ?? null}
        features={features}
        onClose={() => { setDialogOpen(false); setEditing(null); }}
        onSaved={fetchRecords}
      />
    </Box>
  );
};

export default RecordManagement;
